/**
 * Core Multiplayer Types for RollWords Online Multiplayer
 *
 * Defines TypeScript interfaces for WebSocket events, data models,
 * and shared types between client and server.
 */
// ==================== Enums ====================
export var SessionPhase;
(function (SessionPhase) {
  SessionPhase['WAITING'] = 'WAITING';
  SessionPhase['PLAYING'] = 'PLAYING';
  SessionPhase['FINISHED'] = 'FINISHED';
  SessionPhase['EXPIRED'] = 'EXPIRED';
})(SessionPhase || (SessionPhase = {}));
export var PlayerRole;
(function (PlayerRole) {
  PlayerRole['PLAYER'] = 'PLAYER';
  PlayerRole['SPECTATOR'] = 'SPECTATOR';
})(PlayerRole || (PlayerRole = {}));
export var ConnectionStatus;
(function (ConnectionStatus) {
  ConnectionStatus['CONNECTED'] = 'CONNECTED';
  ConnectionStatus['DISCONNECTED'] = 'DISCONNECTED';
  ConnectionStatus['RECONNECTING'] = 'RECONNECTING';
})(ConnectionStatus || (ConnectionStatus = {}));
export var JoinError;
(function (JoinError) {
  JoinError['INVALID_CODE'] = 'INVALID_CODE';
  JoinError['SESSION_FULL'] = 'SESSION_FULL';
  JoinError['NAME_TAKEN'] = 'NAME_TAKEN';
  JoinError['SESSION_EXPIRED'] = 'SESSION_EXPIRED';
})(JoinError || (JoinError = {}));
export var DisconnectReason;
(function (DisconnectReason) {
  DisconnectReason['CLIENT_DISCONNECT'] = 'client_disconnect';
  DisconnectReason['SERVER_ERROR'] = 'server_error';
  DisconnectReason['TIMEOUT'] = 'timeout';
})(DisconnectReason || (DisconnectReason = {}));
export var PlayerLeaveReason;
(function (PlayerLeaveReason) {
  PlayerLeaveReason['DISCONNECTED'] = 'DISCONNECTED';
  PlayerLeaveReason['LEFT_VOLUNTARILY'] = 'LEFT_VOLUNTARILY';
})(PlayerLeaveReason || (PlayerLeaveReason = {}));
// ==================== Game Action Types ====================
export var GameActionType;
(function (GameActionType) {
  // Pool actions
  GameActionType['BALL_LAUNCH'] = 'BALL_LAUNCH';
  GameActionType['BALL_AIM'] = 'BALL_AIM';
  GameActionType['BALL_POWER_SET'] = 'BALL_POWER_SET';
  GameActionType['TURN_PROGRESS'] = 'TURN_PROGRESS';
  // Word actions
  GameActionType['LETTER_PLACE'] = 'LETTER_PLACE';
  GameActionType['LETTER_REMOVE'] = 'LETTER_REMOVE';
  GameActionType['WORD_SUBMIT'] = 'WORD_SUBMIT';
  GameActionType['WORD_CHALLENGE'] = 'WORD_CHALLENGE';
  // Turn actions
  GameActionType['TURN_END'] = 'TURN_END';
  GameActionType['TURN_SKIP'] = 'TURN_SKIP';
  GameActionType['RACK_GENERATED'] = 'RACK_GENERATED';
})(GameActionType || (GameActionType = {}));
export var TurnChangeReason;
(function (TurnChangeReason) {
  TurnChangeReason['COMPLETED_TURN'] = 'COMPLETED_TURN';
  TurnChangeReason['TIME_EXPIRED'] = 'TIME_EXPIRED';
  TurnChangeReason['PLAYER_SKIP'] = 'PLAYER_SKIP';
  TurnChangeReason['PLAYER_LEFT'] = 'PLAYER_LEFT';
})(TurnChangeReason || (TurnChangeReason = {}));
// ==================== Session Payload Error Codes ====================
export var SessionPayloadErrorCode;
(function (SessionPayloadErrorCode) {
  SessionPayloadErrorCode['INVALID_PAYLOAD'] = 'INVALID_PAYLOAD';
  SessionPayloadErrorCode['INVALID_DISPLAY_NAME'] = 'INVALID_DISPLAY_NAME';
  SessionPayloadErrorCode['INVALID_SHARE_CODE'] = 'INVALID_SHARE_CODE';
  SessionPayloadErrorCode['INVALID_MAX_PLAYERS'] = 'INVALID_MAX_PLAYERS';
  SessionPayloadErrorCode['INVALID_SETTINGS'] = 'INVALID_SETTINGS';
})(SessionPayloadErrorCode || (SessionPayloadErrorCode = {}));
// ==================== Share Code Utilities ====================
export const SHARE_CODE_ALPHABET = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
export const SHARE_CODE_LENGTH = 4;
export const SHARE_CODE_COMBINATIONS = Math.pow(26, 4); // 456,976
// ==================== Validation Constants ====================
export const DISPLAY_NAME_MAX_LENGTH = 20;
export const SESSION_MIN_PLAYERS = 2;
export const SESSION_MAX_PLAYERS = 4;
// ==================== Payload Validation ====================
const SHARE_CODE_PATTERN = /^[A-Z]{4}$/;
function asRecord(payload) {
  if (!payload || typeof payload !== 'object' || Array.isArray(payload)) {
    return null;
  }
  return payload;
}
function validationError(code, message, field, details) {
  return { code, message, field, details };
}
function normalizeDisplayName(raw) {
  if (typeof raw !== 'string') {
    return null;
  }
  const normalized = raw.trim();
  if (normalized.length < 1 || normalized.length > DISPLAY_NAME_MAX_LENGTH) {
    return null;
  }
  return normalized;
}
export function validateCreateSessionPayload(payload) {
  const errors = [];
  const record = asRecord(payload);
  if (!record) {
    return {
      isValid: false,
      errors: [
        validationError(
          SessionPayloadErrorCode.INVALID_PAYLOAD,
          'Create session payload must be an object.',
        ),
      ],
    };
  }
  const displayName = normalizeDisplayName(record['displayName']);
  if (!displayName) {
    errors.push(
      validationError(
        SessionPayloadErrorCode.INVALID_DISPLAY_NAME,
        `Display name is required and must be 1-${DISPLAY_NAME_MAX_LENGTH} characters.`,
        'displayName',
      ),
    );
  }
  let maxPlayers;
  if (record['maxPlayers'] !== undefined) {
    if (typeof record['maxPlayers'] !== 'number' || !Number.isInteger(record['maxPlayers'])) {
      errors.push(
        validationError(
          SessionPayloadErrorCode.INVALID_MAX_PLAYERS,
          'maxPlayers must be an integer.',
          'maxPlayers',
          record['maxPlayers'],
        ),
      );
    } else if (
      record['maxPlayers'] < SESSION_MIN_PLAYERS ||
      record['maxPlayers'] > SESSION_MAX_PLAYERS
    ) {
      errors.push(
        validationError(
          SessionPayloadErrorCode.INVALID_MAX_PLAYERS,
          `maxPlayers must be between ${SESSION_MIN_PLAYERS} and ${SESSION_MAX_PLAYERS}.`,
          'maxPlayers',
          record['maxPlayers'],
        ),
      );
    } else {
      maxPlayers = record['maxPlayers'];
    }
  }
  let settings;
  if (record['settings'] !== undefined) {
    const settingsRecord = asRecord(record['settings']);
    if (!settingsRecord) {
      errors.push(
        validationError(
          SessionPayloadErrorCode.INVALID_SETTINGS,
          'settings must be an object when provided.',
          'settings',
          record['settings'],
        ),
      );
    } else {
      settings = settingsRecord;
    }
  }
  if (errors.length > 0 || !displayName) {
    return { isValid: false, errors };
  }
  return { isValid: true, value: { displayName, maxPlayers, settings }, errors: [] };
}
export function validateJoinSessionPayload(payload) {
  const errors = [];
  const record = asRecord(payload);
  if (!record) {
    return {
      isValid: false,
      errors: [
        validationError(
          SessionPayloadErrorCode.INVALID_PAYLOAD,
          'Join session payload must be an object.',
        ),
      ],
    };
  }
  const displayName = normalizeDisplayName(record['displayName']);
  if (!displayName) {
    errors.push(
      validationError(
        SessionPayloadErrorCode.INVALID_DISPLAY_NAME,
        `Display name is required and must be 1-${DISPLAY_NAME_MAX_LENGTH} characters.`,
        'displayName',
      ),
    );
  }
  let shareCode = null;
  if (typeof record['shareCode'] === 'string') {
    const normalizedShareCode = record['shareCode'].trim().toUpperCase();
    if (SHARE_CODE_PATTERN.test(normalizedShareCode)) {
      shareCode = normalizedShareCode;
    }
  }
  if (!shareCode) {
    errors.push(
      validationError(
        SessionPayloadErrorCode.INVALID_SHARE_CODE,
        `shareCode must be ${SHARE_CODE_LENGTH} uppercase letters.`,
        'shareCode',
        record['shareCode'],
      ),
    );
  }
  if (errors.length > 0 || !displayName || !shareCode) {
    return { isValid: false, errors };
  }
  return { isValid: true, value: { shareCode, displayName }, errors: [] };
}
